/** Maps Firebase Auth error codes to user-friendly messages. */

const MESSAGES: Record<string, string> = {
  'auth/invalid-email': 'That email address looks invalid.',
  'auth/user-disabled': 'This account has been disabled. Contact support.',
  'auth/user-not-found': 'No account found with that email.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/invalid-login-credentials': 'Incorrect email or password.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/weak-password': 'Password is too weak — use at least 8 characters.',
  'auth/missing-password': 'Please enter a password.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/network-request-failed': 'Network error. Check your connection and try again.',
  'auth/requires-recent-login': 'Please re-enter your password to continue.',
  'auth/popup-closed-by-user': 'The sign-in window was closed before completing.',
  'auth/expired-action-code': 'This link has expired. Please request a new one.',
  'auth/invalid-action-code': 'This link is invalid or has already been used.',
  'auth/user-token-expired': 'Your session expired. Please sign in again.',
  'auth/operation-not-allowed': 'Email/password sign-in is not enabled for this project.',
};

interface FirebaseLikeError {
  code?: string;
  message?: string;
}

/** Extract a friendly message from any thrown auth error. */
export function friendlyAuthError(err: unknown): string {
  const e = err as FirebaseLikeError;
  if (e?.code && MESSAGES[e.code]) return MESSAGES[e.code];
  if (e?.message) {
    // Some SDK errors embed the code in the message string.
    const match = e.message.match(/\(auth\/[^)]+\)/);
    if (match) {
      const code = match[0].slice(1, -1);
      if (MESSAGES[code]) return MESSAGES[code];
    }
    return e.message.replace(/^Firebase:\s*/, '');
  }
  return 'Something went wrong. Please try again.';
}

/** True when the error indicates the user must re-authenticate. */
export function needsReauth(err: unknown): boolean {
  return (err as FirebaseLikeError)?.code === 'auth/requires-recent-login';
}
